<template>
	<div class="math-formula-input">
		<div class="position-relative">
			<textarea
				ref="textarea"
				class="form-control"
				:class="{ 'border-warning': warnings.length }"
				:style="textareaStyle"
				:placeholder="placeholder"
				:value="value"
				spellcheck="false"
				autocomplete="off"
				@input="onInput"
				@keydown="onKeydown"
				@click="updateCompletions"
				@blur="closeCompletions"
			></textarea>
			<ul v-if="completions.length" class="list-group math-formula-completions shadow-sm">
				<li
					v-for="(completion, i) in completions"
					:key="completion.label + (completion.detail || '')"
					class="list-group-item list-group-item-action py-1 px-2"
					:class="{ active: i === activeCompletion }"
					@mousedown.prevent="applyCompletion(completion)"
					@mouseenter="activeCompletion = i"
				>
					<code>{{ completion.label }}</code>
					<small v-if="completion.detail" class="ml-2" :class="i === activeCompletion ? 'text-white' : 'text-muted'">
						{{ completion.detail === 'this-element' ? $t('math-formula-this-element') : completion.detail }}
					</small>
				</li>
			</ul>
		</div>
		<ul v-if="warnings.length" class="list-unstyled small text-warning mt-1 mb-2">
			<li v-for="(warning, i) in warnings" :key="i">
				<b-icon icon="exclamation-triangle" class="mr-1"></b-icon>{{ warningMessage(warning) }}
				<a v-if="warning.suggestion" href="#" @mousedown.prevent="applySuggestion(warning)">
					{{ $t('math-formula-did-you-mean', { suggestion: warning.suggestion }) }}
				</a>
			</li>
		</ul>
	</div>
</template>

<script>
import {
	validateCustomProperty,
	getAutocompleteContext,
	getCompletions,
} from "src/include/mathreferences";

export default {
	props: {
		value: { type: String, default: '' },
		// lookup built with buildIndex() from the propertyreference endpoint
		index: { type: Map, required: true },
		// { name, properties } of the element being edited
		self: { type: Object, default: null },
		// property names already on the element, to warn about duplicates when adding
		existingNames: { type: Array, default: null },
		placeholder: { type: String, default: '' },
		textareaStyle: { type: [String, Object], default: null },
	},
	data() {
		return {
			completions: [],
			activeCompletion: 0,
			context: null,
		};
	},
	computed: {
		warnings() {
			return validateCustomProperty(this.value, this.index, {
				self: this.self,
				existingNames: this.existingNames,
			});
		},
	},
	methods: {
		onInput(event) {
			this.$emit('input', event.target.value);
			this.$nextTick(this.updateCompletions);
		},
		updateCompletions() {
			const textarea = this.$refs.textarea;
			this.context = getAutocompleteContext(textarea.value, textarea.selectionStart);
			this.completions = getCompletions(this.context, this.index, this.self);
			this.activeCompletion = 0;
		},
		closeCompletions() {
			this.completions = [];
			this.context = null;
		},
		onKeydown(event) {
			if (!this.completions.length) {
				return;
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				this.activeCompletion = (this.activeCompletion + 1) % this.completions.length;
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				this.activeCompletion = (this.activeCompletion - 1 + this.completions.length) % this.completions.length;
			} else if (event.key === 'Enter' || event.key === 'Tab') {
				event.preventDefault();
				this.applyCompletion(this.completions[this.activeCompletion]);
			} else if (event.key === 'Escape') {
				event.preventDefault();
				this.closeCompletions();
			}
		},
		applyCompletion(completion) {
			const textarea = this.$refs.textarea;
			const caret = textarea.selectionStart;
			let after = this.value.substring(caret);
			// replace the rest of a word the caret is inside, and don't double up a closing brace
			after = after.replace(/^[A-Za-z0-9_-]*/, '');
			if (completion.closes && after.startsWith('}')) {
				after = after.substring(1);
			}
			const before = this.value.substring(0, this.context.from);
			const newValue = before + completion.insert + after;
			const newCaret = before.length + completion.insert.length;
			this.$emit('input', newValue);
			this.$nextTick(() => {
				textarea.focus();
				textarea.setSelectionRange(newCaret, newCaret);
				// after picking an element, go straight on to its properties
				if (completion.closes) {
					this.closeCompletions();
				} else {
					this.updateCompletions();
				}
			});
		},
		applySuggestion(warning) {
			// raw is "${scope.property}", "${property}" or a bare "scope.property" stats argument
			const fixed = warning.type === 'unknown-element'
				? warning.raw.replace(warning.scope + '.', warning.suggestion + '.')
				: warning.raw.substring(0, warning.raw.lastIndexOf(warning.property)) + warning.suggestion + (warning.raw.endsWith('}') ? '}' : '');
			this.$emit('input', this.value.split(warning.raw).join(fixed));
		},
		warningMessage(warning) {
			switch (warning.type) {
				case 'missing-name':
					return this.$t('math-formula-missing-name');
				case 'duplicate-name':
					return this.$t('math-formula-duplicate-name', { name: warning.property });
				case 'malformed':
					return this.$t('math-formula-malformed-reference', { reference: warning.raw });
				case 'unknown-element':
					return this.$t('math-formula-unknown-element', { element: warning.scope, reference: warning.raw });
				case 'unknown-property':
					return this.$t('math-formula-unknown-property', { element: warning.scope, property: warning.property });
				case 'unknown-self-property':
					return this.$t('math-formula-unknown-self-property', { property: warning.property });
			}
			return warning.raw;
		},
	},
};
</script>

<style scoped>
.math-formula-completions {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	z-index: 1000;
	max-height: 260px;
	overflow-y: auto;
}
.math-formula-completions .list-group-item {
	cursor: pointer;
}
.math-formula-completions .list-group-item.active code {
	color: inherit;
}
</style>
